import { screenMd, screenSm } from 'constants/styles/breakpoints';
import styled, { css, keyframes } from 'styled-components';

export const Card = styled.article`
  display: flex;
  flex-direction: column;
  padding: 20px;
  align-items: flex-start;
  gap: 12px;
  align-self: stretch;
  border-radius: 10px;
  border: 1px solid var(--dusty-gray);
  background: var(--white);
  position: relative;
  width: 100%;
  min-height: 170px;

  ${screenSm} {
    flex-direction: row;
    align-items: flex-start;
  }
`;

export const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  min-width: 0;

  ${screenSm} {
    flex: 1;
  }
`;

export const Title = styled.h3`
  margin: 0;
  overflow: hidden;
  color: var(--black-dark);
  font-feature-settings: 'liga' off, 'clig' off;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: var(--font-family-base);
  font-size: 18px;
  font-style: normal;
  font-weight: 900;
  line-height: 24px;
`;

export const Tags = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

export const ContentTypeTag = styled.span<{ $bgColor: string; $textColor: string }>`
  display: inline-flex;
  padding: 4px 6px;
  align-items: center;
  gap: 3px;
  border-radius: 3px;
  background: ${({ $bgColor }) => $bgColor};
  color: ${({ $textColor }) => $textColor};
  font-feature-settings: 'liga' off, 'clig' off;
  font-family: var(--font-family-base);
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 14px;
`;

export const DetailValue = styled.span`
  color: var(--light-gray-darker);
  font-feature-settings: 'liga' off, 'clig' off;
  font-family: var(--font-family-base);
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 14px;
`;

export const Divider = styled.span`
  width: 1px;
  height: 22px;
  background: var(--grey-lighter);
`;

export const Description = styled.p<{ $hasMetricsColumn?: boolean }>`
  margin: 0;
  width: 100%;
  color: var(--light-gray-darker);
  font-feature-settings: 'liga' off, 'clig' off;
  font-family: var(--font-family-base);
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;
  max-height: 80px;

  ${screenMd} {
    width: ${({ $hasMetricsColumn }) => ($hasMetricsColumn ? '100%' : '82%')};
  }
`;

export const Added = styled.span`
  color: var(--light-gray-darker);
  font-feature-settings: 'liga' off, 'clig' off;
  font-family: var(--font-family-base);
  font-size: 10px;
  font-style: normal;
  font-weight: 400;
  line-height: 12px;
`;

export const MetricsColumn = styled.div`
  width: 100%;

  ${screenSm} {
    width: 210px;
    min-width: 210px;
    max-width: 210px;
    margin-left: auto;
  }
`;

const metricsFadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const MetricsPanel = styled.section`
  display: flex;
  flex-direction: column;
  width: 100%;
  border-top: 1px solid var(--grey-lighter);
  border-radius: 10px;
  background: linear-gradient(
    112deg,
    rgba(187, 188, 199, 0.11) -46.39%,
    rgba(187, 188, 199, 0) 112.38%
  );
  padding: 10px;
  gap: 4px;
  animation: ${metricsFadeIn} 180ms ease-out;
`;

export const MetricsTitleWrapper = styled.div`
  display: flex;
  padding-bottom: 5px;
  justify-content: center;
  align-items: center;
  gap: 3px;
  align-self: stretch;
`;

export const MetricsTitleIcon = styled.span`
  display: flex;
  width: 22px;
  height: 22px;
  padding: 4px 5px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  aspect-ratio: 1 / 1;
  border-radius: 50px;
  background: #bb85ff;
  flex-shrink: 0;
`;

export const MetricsTitle = styled.h4`
  margin: 0;
  color: var(--black-dark);
  font-family: var(--font-family-base);
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 14px;
`;

export const MetricRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
`;

export const MetricLabelGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  min-width: 0;
  flex: 1;
`;

export const MetricInfoIcon = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
`;

export const MetricLabel = styled.span`
  color: var(--light-gray-darker);
  font-family: var(--font-family-base);
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 14px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const MetricValue = styled.span`
  color: var(--black-dark);
  text-align: right;
  font-family: var(--font-family-base);
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const MetricDivider = styled.span`
  display: block;
  width: 100%;
  height: 1px;
  background: #e9e9ed;
`;

export const VisibilitySection = styled.div`
  margin-top: 8px;
  position: relative;
  width: fit-content;
`;

const visibilityPillBase = css`
  display: flex;
  padding: 6px 12px;
  align-items: flex-start;
  gap: 10px;
  border-radius: 40px;
  background: var(--grey-lighter, #f2f2f7);
  color: var(--light-gray-darker, #57575c);
  font-feature-settings: 'liga' off, 'clig' off;
  font-family: var(--font-family-base, Lato);
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 14px;
`;

export const VisibilityButton = styled.button<{ $isOpen?: boolean }>`
  ${visibilityPillBase}
  border: 2px solid transparent;
  cursor: pointer;
  transition: border-color 150ms ease, background-color 150ms ease;

  &:hover:not(:disabled) {
    border-color: var(--lilac-lighter, #d3c9fa);
    background: #f5f8fa;
  }

  &:focus-visible:not(:disabled),
  &:active:not(:disabled) {
    border-color: var(--lilac-lighter, #d3c9fa);
    background: var(--grey-lighter, #f2f2f7);
  }

  ${({ $isOpen }) =>
    $isOpen &&
    css`
      border-color: var(--lilac-lighter, #d3c9fa);
      background: var(--grey-lighter, #f2f2f7);
    `}

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

export const VisibilityReadOnlyPill = styled.div`
  ${visibilityPillBase}
`;

export const VisibilityButtonChevron = styled.span`
  display: inline-flex;
  width: 16px;
  height: 16px;
  align-items: center;
  justify-content: center;
  flex: 0 0 16px;
  color: var(--light-gray-darker, #57575c);
`;

export const VisibilityDropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 2;
  display: flex;
  width: 275px;
  padding: 20px;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  border-radius: 10px;
  border: 1px solid rgba(151, 71, 255, 0.5);
  background: var(--Primary-INE-White, #fff);
  box-shadow: 0 30px 48px 0 rgba(0, 0, 0, 0.2);
`;

export const VisibilityOptionIndicator = styled.span.attrs<{ $selected: boolean }>(
  ({ $selected }) => ({
    'data-selected': $selected ? 'true' : 'false',
  })
)<{ $selected: boolean }>`
  width: 20px;
  height: 20px;
  flex: 0 0 20px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
  background: var(--grey-lighter, #f2f2f7);
  transition: background-color 150ms ease, border-color 150ms ease;

  &::after {
    content: '';
    width: ${({ $selected }) => ($selected ? '10px' : '0')};
    height: ${({ $selected }) => ($selected ? '10px' : '0')};
    border-radius: 50%;
    background: var(--lilac, #a793f6);
  }
`;

export const VisibilityOption = styled.button`
  display: flex;
  padding: 5px;
  align-items: flex-start;
  gap: 5px;
  align-self: stretch;
  width: 100%;
  text-align: left;
  border: none;
  border-radius: 5px;
  background: transparent;
  cursor: pointer;
  transition: background-color 150ms ease;

  &:hover:not(:disabled),
  &:focus-visible:not(:disabled) {
    background: var(--grey-lighter, #f2f2f7);
  }

  &:hover:not(:disabled)
    ${VisibilityOptionIndicator},
    &:focus-visible:not(:disabled)
    ${VisibilityOptionIndicator} {
    background: rgba(255, 255, 255, 0.6);
    border-color: var(--lilac, #a793f6);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

export const VisibilityOptionContent = styled.div`
  display: block;
  min-width: 0;
`;

export const VisibilityOptionLabel = styled.span`
  color: var(--black-dark, #1a2535);
  font-feature-settings: 'liga' off, 'clig' off;
  font-family: var(--font-family-base, Lato);
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
  display: inline;
  margin-right: 6px;
`;

export const VisibilityDescription = styled.span`
  color: var(--light-gray-darker, #57575c);
  font-feature-settings: 'liga' off, 'clig' off;
  font-family: var(--font-family-base, Lato);
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  display: inline;
`;
